import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Wallet } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AmountText, toneForTransactionType, type AmountTone } from '@/components/finance-v2/FinanceBadges'
import { cn } from '@/utils/cn'
import { financeSummaryService, type EntitySummary, type SideSummary } from '@/services/financeSummaryService'
import { dueInLabel, formatBRL, formatIsoDate } from '@/lib/finance'

type Target = { clientId: string; caseId?: never } | { caseId: string; clientId?: never }

function Side({ title, side }: { title: string; side: SideSummary }) {
  const rows: Array<[string, { count: number; total: number }, AmountTone]> = [
    ['Pago', side.paid, 'neutral'],
    ['A vencer', side.pending, 'neutral'],
    ['Vencido', side.overdue, 'expense'],
  ]
  return (
    <div className="min-w-0">
      <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">{title}</div>
      <dl className="flex flex-col gap-1">
        {rows.map(([label, mc, tone]) => (
          <div key={label} className="flex justify-between gap-2 text-sm">
            <dt className="text-muted-foreground">{label}{mc.count > 0 ? ` (${mc.count})` : ''}</dt>
            <dd><AmountText tone={tone}>{formatBRL(mc.total)}</AmountText></dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * Resumo financeiro (pago / a vencer / vencido) na ficha do cliente ou do caso.
 * Lê do financeiro pela API pública (/summary/client|case); quem não tem
 * permissão de financeiro (403) ou erro → o cartão não aparece.
 */
/** `hideWhenEmpty`: no dossiê, bloco sem lançamento nenhum não ocupa espaço. */
export function FinanceSummaryCard(props: Target & { className?: string; hideWhenEmpty?: boolean }) {
  const [summary, setSummary] = useState<EntitySummary | null>(null)
  const id = props.clientId ?? props.caseId
  const kind = props.clientId ? 'clientId' : 'caseId'

  useEffect(() => {
    let alive = true
    setSummary(null)
    const load = props.clientId ? financeSummaryService.client(props.clientId) : financeSummaryService.case(props.caseId!)
    load.then((s) => { if (alive) setSummary(s) }).catch(() => { if (alive) setSummary(null) })
    return () => { alive = false }
  }, [props.clientId, props.caseId])

  if (!summary) return null
  const empty = [summary.receivable, summary.payable].every((s) => s.paid.count + s.pending.count + s.overdue.count === 0)
  if (empty && props.hideWhenEmpty) return null

  return (
    <section className={cn('min-w-0', props.className)} aria-label="Resumo financeiro">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Wallet className="size-4 text-muted-foreground" aria-hidden="true" />
            <h3>Financeiro</h3>
          </CardTitle>
          <Link to={`/finance-v2/transactions?${kind}=${id}`} className="text-xs text-primary underline-offset-4 hover:underline">Ver lançamentos</Link>
        </CardHeader>
        <CardContent className="min-w-0">
          {empty ? (
            <p className="text-sm text-muted-foreground">Nenhum lançamento vinculado.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Side title="A receber" side={summary.receivable} />
                <Side title="A pagar" side={summary.payable} />
              </div>
              {summary.upcoming.length > 0 && (
                <ul className="mt-3 flex flex-col gap-1 border-t border-border pt-3">
                  {summary.upcoming.map((u) => (
                    <li key={u.id} className="flex min-w-0 justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate">
                        {u.description}
                        <span className={cn('ml-2 text-xs', u.overdue ? 'text-destructive' : 'text-muted-foreground')}>
                          {formatIsoDate(u.dueDate)} · {dueInLabel(u.daysUntilDue)}
                        </span>
                      </span>
                      <AmountText tone={toneForTransactionType(u.type)} className="shrink-0">{formatBRL(u.amount)}</AmountText>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
